import React, { useEffect, useState } from 'react';
import { authService } from '../../services/api';
import { EmployeeRecord, RegisterRequest, UpdateUserRequest, UserRole } from '../../types/auth';
import styles from './UserRegistration.module.css';

const PencilIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.actionIcon}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const BanIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.actionIcon}>
    <circle cx="12" cy="12" r="10" />
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.actionIcon}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function isValidCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const calcDigit = (slice: string, factor: number): number => {
    const sum = slice.split('').reduce((acc, d, i) => acc + Number(d) * (factor - i), 0);
    const rem = (sum * 10) % 11;
    return rem >= 10 ? 0 : rem;
  };

  return (
    calcDigit(digits.slice(0, 9), 10) === Number(digits[9]) &&
    calcDigit(digits.slice(0, 10), 11) === Number(digits[10])
  );
}

function formatCPF(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

function maskCPF(cpf: string): string {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
}

function formatCEP(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/(\d{5})(\d{1,3})$/, '$1-$2');
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
}

function formatAddress(employee: EmployeeRecord): string {
  const firstLine = [employee.rua, employee.numero].filter(Boolean).join(', ');
  const secondLine = [employee.bairro, employee.cidade, employee.estado].filter(Boolean).join(' - ');
  const cep = employee.cep ? `CEP ${employee.cep}` : '';
  return [firstLine, secondLine, cep].filter(Boolean).join(' | ');
}

const initialForm: RegisterRequest = {
  email: '',
  password: '',
  role: UserRole.TECNICO,
  nome: '',
  cpf: '',
  telefone: '',
  rua: '',
  numero: '',
  bairro: '',
  cep: '',
  cidade: '',
  estado: '',
};

const initialEditForm: UpdateUserRequest = {
  nome: '',
  email: '',
  cpf: '',
  telefone: '',
  rua: '',
  numero: '',
  bairro: '',
  cep: '',
  cidade: '',
  estado: '',
  role: UserRole.TECNICO,
};

const UserRegistration: React.FC = () => {
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [employeesError, setEmployeesError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<RegisterRequest>(initialForm);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit modal state
  const [editEmployee, setEditEmployee] = useState<EmployeeRecord | null>(null);
  const [editForm, setEditForm] = useState<UpdateUserRequest>(initialEditForm);
  const [editConfirmPassword, setEditConfirmPassword] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    setEmployeesError('');
    try {
      const response = await authService.listUsers();
      setEmployees(response);
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Falha ao carregar usuários.');
    } finally {
      setLoadingEmployees(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const openModal = () => {
    setShowModal(true);
    setForm(initialForm);
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const closeModal = () => {
    if (loading) return;
    setShowModal(false);
    setForm(initialForm);
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const handleChange = <K extends keyof RegisterRequest>(field: K, value: RegisterRequest[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!form.nome?.trim()) {
      setError('Informe o nome completo do usuário.');
      return;
    }

    if (!form.email.trim() || !form.password.trim()) {
      setError('Preencha e-mail e senha para cadastrar o usuário.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      setError('Informe um e-mail válido (ex: usuario@prefeitura.gov.br).');
      return;
    }

    if (form.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (form.password !== confirmPassword) {
      setError('A confirmação de senha não confere.');
      return;
    }

    const cpfRaw = form.cpf?.trim() ?? '';
    if (cpfRaw !== '' && !isValidCPF(cpfRaw)) {
      setError('CPF inválido. Verifique os dígitos informados.');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.register({
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        nome: form.nome?.trim(),
        cpf: form.cpf?.trim(),
        telefone: form.telefone?.trim(),
        rua: form.rua?.trim(),
        numero: form.numero?.trim(),
        bairro: form.bairro?.trim(),
        cep: form.cep?.trim(),
        cidade: form.cidade?.trim(),
        estado: form.estado?.trim(),
      });

      setSuccess(response.message || 'Usuário cadastrado com sucesso.');
      await loadEmployees();
      closeModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao cadastrar usuário.');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (employee: EmployeeRecord) => {
    setEditEmployee(employee);
    setEditForm({
      nome: employee.nome || '',
      email: employee.email,
      cpf: employee.cpf ? formatCPF(employee.cpf) : '',
      telefone: employee.telefone ? formatPhone(employee.telefone) : '',
      rua: employee.rua || '',
      numero: employee.numero || '',
      bairro: employee.bairro || '',
      cep: employee.cep ? formatCEP(employee.cep) : '',
      cidade: employee.cidade || '',
      estado: employee.estado || '',
      role: employee.role as UserRole,
    });
    setEditConfirmPassword('');
    setEditError('');
    setEditSuccess('');
  };

  const closeEditModal = () => {
    if (editLoading) return;
    setEditEmployee(null);
    setEditConfirmPassword('');
    setEditError('');
    setEditSuccess('');
  };

  const handleEditChange = <K extends keyof UpdateUserRequest>(field: K, value: UpdateUserRequest[K]) => {
    setEditForm((current) => ({ ...current, [field]: value }));
  };

  const handleEditSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editEmployee) return;
    setEditError('');
    setEditSuccess('');

    if (!editForm.nome?.trim()) {
      setEditError('Informe o nome completo do usuário.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (editForm.email && !emailRegex.test(editForm.email.trim())) {
      setEditError('Informe um e-mail válido.');
      return;
    }

    const cpfRaw = editForm.cpf?.trim() ?? '';
    if (cpfRaw !== '' && !isValidCPF(cpfRaw)) {
      setEditError('CPF inválido. Verifique os dígitos informados.');
      return;
    }

    const newPassword = editForm.password?.trim() ?? '';
    if (newPassword !== '') {
      if (newPassword.length < 6) {
        setEditError('A senha deve ter pelo menos 6 caracteres.');
        return;
      }
      if (newPassword !== editConfirmPassword) {
        setEditError('A confirmação de senha não confere.');
        return;
      }
    }

    setEditLoading(true);
    try {
      await authService.updateUser(editEmployee.id, {
        nome: editForm.nome?.trim() ?? '',
        email: editForm.email.trim(),
        cpf: editForm.cpf?.trim(),
        telefone: editForm.telefone?.trim(),
        rua: editForm.rua?.trim(),
        numero: editForm.numero?.trim(),
        bairro: editForm.bairro?.trim(),
        cep: editForm.cep?.trim(),
        cidade: editForm.cidade?.trim(),
        estado: editForm.estado?.trim(),
        role: editForm.role,
        password: newPassword || undefined,
      });
      setEditSuccess('Usuário atualizado com sucesso.');
      await loadEmployees();
      closeEditModal();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Falha ao atualizar usuário.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleStatus = async (employee: EmployeeRecord) => {
    const newStatus = !(employee.ativo !== false);
    const action = newStatus ? 'ativar' : 'inativar';
    if (!window.confirm(`Deseja realmente ${action} o usuário ${employee.nome || employee.email}?`)) return;

    setTogglingId(employee.id);
    try {
      await authService.toggleUserStatus(employee.id, newStatus);
      await loadEmployees();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : `Falha ao ${action} usuário.`);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Usuários</h1>
          <p className={styles.subtitle}>
            Visualize os Usuários cadastrados e abra o modal para incluir novos administradores ou técnicos.
          </p>
        </div>
        <button className={styles.primaryButton} type="button" onClick={openModal}>
          Novo Usuário
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.listHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Usuários cadastrados</h2>
            <p className={styles.sectionDescription}>
              A lista é atualizada após cada cadastro para facilitar conferência de dados e perfis de acesso.
            </p>
          </div>
          <button className={styles.secondaryButton} type="button" onClick={loadEmployees} disabled={loadingEmployees}>
            {loadingEmployees ? 'Atualizando...' : 'Atualizar lista'}
          </button>
        </div>

        {employeesError ? <div className={`${styles.message} ${styles.error}`}>{employeesError}</div> : null}

        {loadingEmployees ? <p className={styles.emptyState}>Carregando usuários...</p> : null}

        {!loadingEmployees && !employees.length ? (
          <p className={styles.emptyState}>Nenhum usuário cadastrado até o momento.</p>
        ) : null}

        {!loadingEmployees && employees.length ? (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Perfil</th>
                  <th>Contato</th>
                  <th>Endereço</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr key={employee.id} className={employee.ativo === false ? styles.inactiveRow : undefined}>
                    <td>
                      <div className={styles.employeeName}>{employee.nome || 'Sem nome informado'}</div>
                      <div className={styles.employeeMeta}>{employee.email}</div>
                      <div className={styles.employeeMeta}>
                        {employee.cpf ? `CPF ${maskCPF(employee.cpf)}` : 'CPF não informado'}
                      </div>
                      {employee.ativo === false && <span className={styles.inactiveBadge}>Inativo</span>}
                    </td>
                    <td>
                      <span className={`${styles.roleBadge} ${employee.role === UserRole.MASTER ? styles.roleAdmin : employee.role === UserRole.ADMIN ? styles.roleAdmin : styles.roleTechnician}`}>
                        {employee.role === UserRole.MASTER ? 'Master' : employee.role === UserRole.ADMIN ? 'Administrador' : 'Técnico'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.employeeMeta}>{employee.telefone ? formatPhone(employee.telefone) : 'Telefone não informado'}</div>
                    </td>
                    <td>
                      <div className={styles.employeeMeta}>{formatAddress(employee) || 'Endereço não informado'}</div>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.editButton}
                          type="button"
                          onClick={() => openEditModal(employee)}
                          aria-label="Editar usuário"
                          title="Editar usuário"
                        >
                          <PencilIcon />
                        </button>
                        <button
                          className={employee.ativo !== false ? styles.inactivateButton : styles.activateButton}
                          type="button"
                          onClick={() => handleToggleStatus(employee)}
                          disabled={togglingId === employee.id}
                          aria-label={employee.ativo !== false ? 'Inativar usuário' : 'Ativar usuário'}
                          title={employee.ativo !== false ? 'Inativar' : 'Ativar'}
                        >
                          {togglingId === employee.id ? '...' : (employee.ativo !== false ? <BanIcon /> : <CheckIcon />)}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2 className={styles.modalTitle}>Cadastrar usuário</h2>
                <p className={styles.modalSubtitle}>
                  Preencha os dados de acesso e identificação do novo usuário.
                </p>
              </div>
              <button className={styles.modalCloseBtn} type="button" onClick={closeModal} aria-label="Fechar">
                ×
              </button>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-nome">Nome completo</label>
              <input
                id="register-nome"
                className={styles.input}
                type="text"
                value={form.nome ?? ''}
                onChange={(event) => handleChange('nome', event.target.value)}
                placeholder="Nome do usuário"
                autoComplete="name"
                  required
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-cpf">CPF</label>
              <input
                id="register-cpf"
                className={styles.input}
                type="text"
                value={form.cpf ?? ''}
                onChange={(event) => handleChange('cpf', formatCPF(event.target.value))}
                placeholder="000.000.000-00"
                maxLength={14}
              />
            </div>
          </div>

          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-telefone">Telefone</label>
              <input
                id="register-telefone"
                className={styles.input}
                type="text"
                value={form.telefone ?? ''}
                onChange={(event) => handleChange('telefone', formatPhone(event.target.value))}
                placeholder="(00) 00000-0000"
                maxLength={15}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-email">E-mail</label>
              <input
                id="register-email"
                className={styles.input}
                type="email"
                value={form.email}
                onChange={(event) => handleChange('email', event.target.value)}
                placeholder="usuario@prefeitura.gov.br"
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="register-rua">Rua / Avenida</label>
            <input
              id="register-rua"
              className={styles.input}
              type="text"
              value={form.rua ?? ''}
              onChange={(event) => handleChange('rua', event.target.value)}
              placeholder="Nome da rua ou avenida"
              autoComplete="street-address"
            />
          </div>

          <div className={styles.grid3}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-numero">Número</label>
              <input
                id="register-numero"
                className={styles.input}
                type="text"
                value={form.numero ?? ''}
                onChange={(event) => handleChange('numero', event.target.value)}
                placeholder="Ex: 123"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-bairro">Bairro</label>
              <input
                id="register-bairro"
                className={styles.input}
                type="text"
                value={form.bairro ?? ''}
                onChange={(event) => handleChange('bairro', event.target.value)}
                placeholder="Nome do bairro"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-cep">CEP</label>
              <input
                id="register-cep"
                className={styles.input}
                type="text"
                value={form.cep ?? ''}
                onChange={(event) => handleChange('cep', formatCEP(event.target.value))}
                placeholder="00000-000"
                maxLength={9}
              />
            </div>
          </div>

          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-cidade">Cidade</label>
              <input
                id="register-cidade"
                className={styles.input}
                type="text"
                value={form.cidade ?? ''}
                onChange={(event) => handleChange('cidade', event.target.value)}
                placeholder="Nome da cidade"
                autoComplete="address-level2"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-estado">Estado (UF)</label>
              <input
                id="register-estado"
                className={styles.input}
                type="text"
                value={form.estado ?? ''}
                onChange={(event) => handleChange('estado', event.target.value.toUpperCase().slice(0, 2))}
                placeholder="Ex: SP"
                maxLength={2}
                autoComplete="address-level1"
              />
            </div>
          </div>

          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-role">Perfil</label>
              <select
                id="register-role"
                className={styles.select}
                value={form.role}
                onChange={(event) => handleChange('role', event.target.value as UserRole)}
              >
                <option value={UserRole.ADMIN}>Administrador</option>
                <option value={UserRole.TECNICO}>Técnico</option>
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="register-password">Senha</label>
              <input
                id="register-password"
                className={styles.input}
                type="password"
                value={form.password}
                onChange={(event) => handleChange('password', event.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="register-confirm-password">Confirmar senha</label>
            <input
              id="register-confirm-password"
              className={styles.input}
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              required
            />
          </div>

          <p className={styles.helpText}>
            Perfis disponíveis: ADMIN para gestão do sistema e TECNICO para execução operacional.
          </p>

          {error ? <div className={`${styles.message} ${styles.error}`}>{error}</div> : null}
          {success ? <div className={`${styles.message} ${styles.success}`}>{success}</div> : null}

          <div className={styles.actions}>
            <button className={styles.secondaryButton} type="button" onClick={closeModal} disabled={loading}>
              Cancelar
            </button>
            <button className={styles.submitButton} type="submit" disabled={loading}>
              {loading ? 'Cadastrando...' : 'Cadastrar usuário'}
            </button>
          </div>
        </form>
          </div>
        </div>
      )}

      {editEmployee && (
        <div className={styles.modalOverlay} onClick={closeEditModal}>
          <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2 className={styles.modalTitle}>Editar usuário</h2>
                <p className={styles.modalSubtitle}>
                  Altere os dados do usuário. Deixe a senha em branco para mantê-la inalterada.
                </p>
              </div>
              <button className={styles.modalCloseBtn} type="button" onClick={closeEditModal} aria-label="Fechar">
                ×
              </button>
            </div>

            <form className={styles.form} onSubmit={handleEditSubmit}>
              <div className={styles.grid}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-nome">Nome completo</label>
                  <input
                    id="edit-nome"
                    className={styles.input}
                    type="text"
                    value={editForm.nome ?? ''}
                    onChange={(event) => handleEditChange('nome', event.target.value)}
                    placeholder="Nome do usuário"
                    required
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-cpf">CPF</label>
                  <input
                    id="edit-cpf"
                    className={styles.input}
                    type="text"
                    value={editForm.cpf ?? ''}
                    onChange={(event) => handleEditChange('cpf', formatCPF(event.target.value))}
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                </div>
              </div>

              <div className={styles.grid}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-telefone">Telefone</label>
                  <input
                    id="edit-telefone"
                    className={styles.input}
                    type="text"
                    value={editForm.telefone ?? ''}
                    onChange={(event) => handleEditChange('telefone', formatPhone(event.target.value))}
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-email">E-mail</label>
                  <input
                    id="edit-email"
                    className={styles.input}
                    type="email"
                    value={editForm.email}
                    onChange={(event) => handleEditChange('email', event.target.value)}
                    placeholder="usuario@prefeitura.gov.br"
                    required
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="edit-rua">Rua / Avenida</label>
                <input
                  id="edit-rua"
                  className={styles.input}
                  type="text"
                  value={editForm.rua ?? ''}
                  onChange={(event) => handleEditChange('rua', event.target.value)}
                  placeholder="Nome da rua ou avenida"
                />
              </div>

              <div className={styles.grid3}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-numero">Número</label>
                  <input
                    id="edit-numero"
                    className={styles.input}
                    type="text"
                    value={editForm.numero ?? ''}
                    onChange={(event) => handleEditChange('numero', event.target.value)}
                    placeholder="Ex: 123"
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-bairro">Bairro</label>
                  <input
                    id="edit-bairro"
                    className={styles.input}
                    type="text"
                    value={editForm.bairro ?? ''}
                    onChange={(event) => handleEditChange('bairro', event.target.value)}
                    placeholder="Nome do bairro"
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-cep">CEP</label>
                  <input
                    id="edit-cep"
                    className={styles.input}
                    type="text"
                    value={editForm.cep ?? ''}
                    onChange={(event) => handleEditChange('cep', formatCEP(event.target.value))}
                    placeholder="00000-000"
                    maxLength={9}
                  />
                </div>
              </div>

              <div className={styles.grid}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-cidade">Cidade</label>
                  <input
                    id="edit-cidade"
                    className={styles.input}
                    type="text"
                    value={editForm.cidade ?? ''}
                    onChange={(event) => handleEditChange('cidade', event.target.value)}
                    placeholder="Nome da cidade"
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-estado">Estado (UF)</label>
                  <input
                    id="edit-estado"
                    className={styles.input}
                    type="text"
                    value={editForm.estado ?? ''}
                    onChange={(event) => handleEditChange('estado', event.target.value.toUpperCase().slice(0, 2))}
                    placeholder="Ex: SP"
                    maxLength={2}
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="edit-role">Perfil</label>
                <select
                  id="edit-role"
                  className={styles.select}
                  value={editForm.role}
                  onChange={(event) => handleEditChange('role', event.target.value as UserRole)}
                >
                  <option value={UserRole.ADMIN}>Administrador</option>
                  <option value={UserRole.TECNICO}>Técnico</option>
                </select>
              </div>

              <div className={styles.grid}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-password">Nova senha</label>
                  <input
                    id="edit-password"
                    className={styles.input}
                    type="password"
                    value={editForm.password ?? ''}
                    onChange={(event) => handleEditChange('password', event.target.value)}
                    placeholder="Deixe em branco para manter"
                    autoComplete="new-password"
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="edit-confirm-password">Confirmar nova senha</label>
                  <input
                    id="edit-confirm-password"
                    className={styles.input}
                    type="password"
                    value={editConfirmPassword}
                    onChange={(event) => setEditConfirmPassword(event.target.value)}
                    placeholder="Repita a nova senha"
                    autoComplete="new-password"
                  />
                </div>
              </div>

              {editError ? <div className={`${styles.message} ${styles.error}`}>{editError}</div> : null}
              {editSuccess ? <div className={`${styles.message} ${styles.success}`}>{editSuccess}</div> : null}

              <div className={styles.actions}>
                <button className={styles.secondaryButton} type="button" onClick={closeEditModal} disabled={editLoading}>
                  Cancelar
                </button>
                <button className={styles.submitButton} type="submit" disabled={editLoading}>
                  {editLoading ? 'Salvando...' : 'Salvar alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRegistration;
